import { useEffect, useRef, useState } from 'react'

import type { RouteStop } from '@/features/field_routes/api'

/**
 * The day's round, drawn: customer pins, the line between them in driving order, and where
 * the rep is standing.
 *
 * Two renderers, and which one you get depends on whether `VITE_GOOGLE_MAPS_API_KEY` is
 * set — the same shape as the backend's two distance providers, for the same reason. Real
 * Google tiles when there is a browser key, a schematic drawn in SVG when there is not.
 *
 * The schematic is not a placeholder. It plots the stops on their real coordinates,
 * normalised into the viewport, so the shape of the round — the loop, the doubling back,
 * the stop that is miles from the others — is honest. What it cannot show is the roads.
 * That is worth being clear about on screen and it is why the fallback is labelled rather
 * than dressed up as a map. It also means this screen works with no key, no billing
 * account and no network, which is the state a rep's phone is in more often than anyone
 * planning the feature expects.
 *
 * Loading the Maps script is deliberately one-shot and cached at module scope: a component
 * that remounts on every tab switch must not append a second `<script>` tag, and the
 * Maps SDK complains loudly (and correctly) if it is loaded twice.
 */

const MAPS_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? ''

interface LatLng {
  lat: number
  lng: number
}

let mapsLoader: Promise<boolean> | null = null

/** Resolves true once the Maps SDK is usable, false if it cannot be loaded at all. */
function loadMaps(): Promise<boolean> {
  if (mapsLoader) return mapsLoader
  if (!MAPS_KEY) {
    mapsLoader = Promise.resolve(false)
    return mapsLoader
  }
  mapsLoader = new Promise<boolean>((resolve) => {
    const existing = (window as unknown as { google?: { maps?: unknown } }).google?.maps
    if (existing) {
      resolve(true)
      return
    }
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(MAPS_KEY)}`
    script.async = true
    script.onload = () => resolve(true)
    // A blocked script, a dead network, a rejected key: all the same answer to the only
    // question the caller has, which is "can I draw with this?".
    script.onerror = () => resolve(false)
    document.head.appendChild(script)
  })
  return mapsLoader
}

export interface RouteMapProps {
  stops: RouteStop[]
  /** Where the rep is now, if a fix is available. */
  current?: LatLng | null
  /** Which stop is highlighted — the one the stop list has swiped to. */
  activeIndex?: number
  onSelectStop?: (index: number) => void
}

export function RouteMap({ stops, current, activeIndex = 0, onSelectStop }: RouteMapProps) {
  const [useGoogle, setUseGoogle] = useState<boolean | null>(MAPS_KEY ? null : false)

  useEffect(() => {
    let cancelled = false
    void loadMaps().then((ok) => {
      if (!cancelled) setUseGoogle(ok)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const located = stops.filter((s) => s.latitude != null && s.longitude != null)

  if (located.length === 0) {
    return (
      <div
        className="flex h-56 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900"
        data-testid="route-map-empty"
      >
        No stop on this round has a location on file yet.
      </div>
    )
  }

  if (useGoogle === null) {
    return (
      <div
        className="h-56 animate-pulse rounded-lg bg-slate-200 dark:bg-slate-800"
        data-testid="route-map-loading"
      />
    )
  }

  return useGoogle ? (
    <GoogleRouteMap stops={located} current={current} activeIndex={activeIndex} />
  ) : (
    <SchematicRouteMap
      stops={located}
      current={current}
      activeIndex={activeIndex}
      onSelectStop={onSelectStop}
    />
  )
}

/* ── Google tiles ──────────────────────────────────────────────────────────── */

// The SDK's own types are not installed (they would pull the whole `@types/google.maps`
// surface in for one screen), so the handful of calls used here are typed structurally.
interface MapsLike {
  Map: new (el: HTMLElement, opts: Record<string, unknown>) => MapInstance
  Marker: new (opts: Record<string, unknown>) => { setMap(map: MapInstance | null): void }
  Polyline: new (opts: Record<string, unknown>) => { setMap(map: MapInstance | null): void }
  LatLngBounds: new () => { extend(point: LatLng): void }
}
interface MapInstance {
  fitBounds(bounds: { extend(point: LatLng): void }, padding?: number): void
}

function GoogleRouteMap({
  stops,
  current,
  activeIndex,
}: {
  stops: RouteStop[]
  current?: LatLng | null
  activeIndex: number
}) {
  const container = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const maps = (window as unknown as { google?: { maps?: MapsLike } }).google?.maps
    if (!maps || !container.current) return

    const path = stops.map((s) => ({ lat: Number(s.latitude), lng: Number(s.longitude) }))
    const map = new maps.Map(container.current, {
      zoom: 12,
      center: path[0],
      disableDefaultUI: true,
      zoomControl: true,
    })

    const drawn: { setMap(map: MapInstance | null): void }[] = []
    path.forEach((point, index) => {
      drawn.push(
        new maps.Marker({
          position: point,
          map,
          // Numbered, because "which one is stop 4" is the only question a rep asks a
          // route map. Ordinary pins all look the same.
          label: { text: String(index + 1), color: '#ffffff', fontSize: '11px' },
          title: stops[index].customer_name,
          zIndex: index === activeIndex ? 100 : 1,
        }),
      )
    })
    drawn.push(new maps.Polyline({ path, map, strokeColor: '#0d9488', strokeWeight: 4 }))
    if (current) {
      drawn.push(
        new maps.Marker({
          position: current,
          map,
          title: 'You are here',
          icon: {
            path: 0, // google.maps.SymbolPath.CIRCLE
            scale: 7,
            fillColor: '#2563eb',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        }),
      )
    }

    const bounds = new maps.LatLngBounds()
    path.forEach((point) => bounds.extend(point))
    if (current) bounds.extend(current)
    map.fitBounds(bounds, 40)

    return () => drawn.forEach((item) => item.setMap(null))
  }, [stops, current, activeIndex])

  return <div ref={container} className="h-56 w-full rounded-lg" data-testid="route-map-google" />
}

/* ── SVG schematic ─────────────────────────────────────────────────────────── */

const VIEW = { width: 320, height: 224, pad: 26 }

function SchematicRouteMap({
  stops,
  current,
  activeIndex,
  onSelectStop,
}: {
  stops: RouteStop[]
  current?: LatLng | null
  activeIndex: number
  onSelectStop?: (index: number) => void
}) {
  const points = stops.map((s) => ({ lat: Number(s.latitude), lng: Number(s.longitude) }))
  const all = current ? [...points, current] : points

  const lats = all.map((p) => p.lat)
  const lngs = all.map((p) => p.lng)
  // A degree of longitude is shorter than a degree of latitude everywhere but the equator,
  // and ignoring that squashes a north-south round into something that looks east-west.
  // Scaling by cos(latitude) keeps the shape of the drive recognisable.
  const midLat = (Math.min(...lats) + Math.max(...lats)) / 2
  const lngScale = Math.cos((midLat * Math.PI) / 180) || 1

  const spanLat = Math.max(...lats) - Math.min(...lats) || 1e-4
  const spanLng = (Math.max(...lngs) - Math.min(...lngs)) * lngScale || 1e-4
  // One scale for both axes, so the drawing keeps its proportions instead of stretching
  // to fill the box.
  const scale = Math.min(
    (VIEW.width - 2 * VIEW.pad) / spanLng,
    (VIEW.height - 2 * VIEW.pad) / spanLat,
  )
  const offsetX = (VIEW.width - spanLng * scale) / 2
  const offsetY = (VIEW.height - spanLat * scale) / 2

  const project = (p: LatLng) => ({
    x: offsetX + (p.lng - Math.min(...lngs)) * lngScale * scale,
    // SVG y grows downward; north should be up.
    y: VIEW.height - offsetY - (p.lat - Math.min(...lats)) * scale,
  })

  const projected = points.map(project)
  const line = projected.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ')
  const me = current ? project(current) : null

  return (
    <div className="relative">
      <svg
        viewBox={`0 0 ${VIEW.width} ${VIEW.height}`}
        className="h-56 w-full rounded-lg border border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900"
        role="img"
        aria-label={`Route with ${stops.length} stops`}
        data-testid="route-map-schematic"
      >
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinejoin="round"
          strokeDasharray="6 4"
          className="text-emerald-500"
        />
        {projected.map((point, index) => (
          <g
            key={stops[index].visit_id}
            onClick={() => onSelectStop?.(index)}
            className={onSelectStop ? 'cursor-pointer' : undefined}
          >
            <circle
              cx={point.x}
              cy={point.y}
              r={index === activeIndex ? 11 : 9}
              className={
                stops[index].status === 'completed'
                  ? 'fill-emerald-600'
                  : index === activeIndex
                    ? 'fill-blue-600'
                    : 'fill-slate-400'
              }
            />
            <text
              x={point.x}
              y={point.y + 3.5}
              textAnchor="middle"
              className="fill-white text-[9px] font-semibold"
            >
              {index + 1}
            </text>
          </g>
        ))}
        {me && (
          <>
            <circle cx={me.x} cy={me.y} r={11} className="fill-blue-500/25" />
            <circle
              cx={me.x}
              cy={me.y}
              r={5}
              className="fill-blue-600 stroke-white"
              strokeWidth={2}
              data-testid="route-map-me"
            />
          </>
        )}
      </svg>
      <p className="mt-1 text-center text-[11px] text-slate-500 dark:text-slate-400">
        Positions are to scale; roads are not shown.
      </p>
    </div>
  )
}
